import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Autocomplete,
  FormControl,
  Typography,
  Input,
  FormHelperText,
  Box,
} from '@mui/material';
import ConfirmationDialog from '../../settings/components/ConfirmationDialog'; // Added ConfirmationDialog import
import { Alert, CircularProgress } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import EmailIcon from '@mui/icons-material/Email';
import { getDocumentUrl } from '../../utils/authUtils';

const COI_ACCEPT = 'application/pdf,image/jpeg,image/png,image/heic,image/webp,.pdf,.jpg,.jpeg,.png,.heic,.webp';
const isImagePath = (p) => /.(jpe?g|png|gif|webp|heic)$/i.test(String(p || ''));

/** Inline preview of the certificate on file: PDFs in a frame, photos as an image. */
function CoiPreview({ documentPath }) {
  const url = getDocumentUrl(documentPath);
  if (!url) return null;
  return (
    <Box sx={{ mt: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden', bgcolor: 'background.default' }}>
      {isImagePath(documentPath) ? (
        <Box component="img" src={url} alt="Certificate of Insurance" sx={{ display: 'block', maxWidth: '100%', maxHeight: 480, mx: 'auto' }} />
      ) : (
        <Box component="iframe" src={url} title="Certificate of Insurance" sx={{ display: 'block', width: '100%', height: { xs: 360, sm: 480 }, border: 0 }} />
      )}
      <Box sx={{ p: 1, display: 'flex', justifyContent: 'flex-end' }}>
        <Button size="small" startIcon={<OpenInNewIcon />} href={url} target="_blank" rel="noopener noreferrer">
          Open in new tab
        </Button>
      </Box>
    </Box>
  );
}

// Removed static options, will be passed via props

function PolicyFormDialog({ open, policyData, policyTypes = [], vendorInfo = null, onClose, onSave, onRequestCoi, requestingCoi = false, coiRequest = null, applicantEmail = '' }) {
  const [formData, setFormData] = useState({});
  const [coiFile, setCoiFile] = useState(null);
  const [addConfirmOpen, setAddConfirmOpen] = useState(false); // State for add confirmation
  const [fileName, setFileName] = useState('');

  // The Add button passes a placeholder id; only a stored policy counts as an edit
  const isEditMode = Boolean(policyData?._id) && policyData._id !== 'new-policy';
  const [validationError, setValidationError] = useState(null);

  useEffect(() => {
    if (open) { // Reset form when dialog opens
      if (isEditMode) {
        // Editing existing policy
        setFormData({
          _id: policyData._id || null, // Keep track of id for saving
          policyType: policyData.policyType || '',
          companyName: policyData.companyName || '',
          policyNumber: policyData.policyNumber || '',
          coverageAmount: policyData.coverageAmount || '',
          effectiveDate: policyData.effectiveDate ? new Date(policyData.effectiveDate).toISOString().split('T')[0] : '',
          expirationDate: policyData.expirationDate ? new Date(policyData.expirationDate).toISOString().split('T')[0] : '',
          contactName: policyData.contactName || '',
          contactEmail: policyData.contactEmail || '', // Assuming these fields exist or are needed
          contactPhone: policyData.contactPhone || '', // Assuming these fields exist or are needed
          street1: policyData.address?.street1 || '',
          street2: policyData.address?.street2 || '',
          city: policyData.address?.city || '',
          state: policyData.address?.state || '',
          zip: policyData.address?.zip || '',
          country: policyData.address?.country || 'US',
          // Note: documentPath is handled separately via file upload state
        });
        // Reset file state when editing
        setCoiFile(null);
        setFileName(policyData.documentPath ? policyData.documentPath.split('/').pop() : ''); // Show existing file name if available
      } else {
        // Adding new policy - initialize with defaults
        setFormData({
          _id: null,
          policyType: policyTypes.length > 0 ? policyTypes[0] : '', // Default to first type or empty
          companyName: '',
          policyNumber: '',
          coverageAmount: '',
          effectiveDate: '',
          expirationDate: '',
          contactName: '',
          contactEmail: '',
          contactPhone: '',
          street1: '',
          street2: '',
          city: '',
          state: '',
          zip: '',
          country: 'US',
        });
        // Reset file state for new policy
        setCoiFile(null);
        setFileName('');
      }
      setValidationError(null);
    }
  }, [policyData, policyTypes, open, isEditMode]); // Rerun effect if dependencies change

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleAutocompleteChange = (name, value) => {
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setCoiFile(file);
      setFileName(file.name);
    } else {
      setCoiFile(null);
      setFileName('');
    }
  };

  const handleSave = () => {
    // Basic client-side validation
    if (!formData.policyType || !formData.companyName || !formData.policyNumber) {
      setValidationError('Policy Type, Company Name and Policy Number are required.');
      return;
    }
    setValidationError(null);

    // Additional confirmation for new policies to prevent wrong vendor assignment
    if (!isEditMode && vendorInfo) {
      // Instead of window.confirm, open the MUI dialog
      setAddConfirmOpen(true);
      return; // Don't proceed with save yet, wait for dialog confirmation
    }

    // If it's edit mode or no vendorInfo (should not happen for add), proceed directly
    proceedWithSave();
  };

  const proceedWithSave = () => {
    const finalPolicyData = {
      ...(isEditMode && { _id: formData._id }), // Include _id only if editing
      policyType: formData.policyType,
      companyName: formData.companyName,
      policyNumber: formData.policyNumber,
      coverageAmount: formData.coverageAmount ? parseFloat(formData.coverageAmount) : null,
      effectiveDate: formData.effectiveDate || null,
      expirationDate: formData.expirationDate || null,
      contactName: formData.contactName,
      contactEmail: formData.contactEmail,
      contactPhone: formData.contactPhone,
      address: {
        street1: formData.street1,
        street2: formData.street2,
        city: formData.city,
        state: formData.state,
        zip: formData.zip,
        country: formData.country,
      },
    };
    // Pass the data and the file (if any) back to the parent
    onSave(finalPolicyData, coiFile);
    // Parent component (InsuranceTab) will handle API calls and closing the dialog
  };

  const handleAddConfirm = () => {
    setAddConfirmOpen(false);
    proceedWithSave();
  };

  const handleAddConfirmClose = () => {
    setAddConfirmOpen(false);
  };

  return (
    <> {/* Added Fragment to hold Dialog and ConfirmationDialog */}
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{isEditMode ? 'Edit Insurance Policy' : 'Add New Insurance Policy'}</DialogTitle>
      <DialogContent>
        {validationError && <Alert severity="error" sx={{ mb: 2 }}>{validationError}</Alert>}
        {/* Vendor Confirmation Section - Only show when adding new policy */}
        {!isEditMode && vendorInfo && (
          <Box sx={{ mb: 3, p: 2, bgcolor: 'primary.light', borderRadius: 1, border: '1px solid', borderColor: 'primary.main' }}>
            <Typography variant="h6" color="primary.contrastText" gutterBottom>
              Adding Policy For:
            </Typography>
            <Typography variant="body1" color="primary.contrastText" sx={{ fontWeight: 'bold' }}>
              {vendorInfo.companyName || 'Unknown Company'}
            </Typography>
            <Typography variant="body2" color="primary.contrastText">
              Vendor ID: {vendorInfo.vendorIdentifier || 'N/A'}
            </Typography>
          </Box>
        )}
        <Grid container spacing={2} sx={{ mt: 1 }}>
          {/* Policy Details */}
          <Grid item xs={12} sm={6}>
            <Autocomplete
              options={policyTypes}
              value={formData.policyType || null}
              onChange={(event, newValue) => handleAutocompleteChange('policyType', newValue)}
              renderInput={(params) => (
                <TextField {...params} label="Policy Type" variant="outlined" fullWidth required />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Company Name"
              name="companyName"
              value={formData.companyName || ''}
              onChange={handleChange}
              fullWidth
              required
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Policy Number"
              name="policyNumber"
              value={formData.policyNumber || ''}
              onChange={handleChange}
              fullWidth
              required
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Coverage Amount ($)"
              name="coverageAmount"
              type="number"
              value={formData.coverageAmount || ''}
              onChange={handleChange}
              fullWidth
              variant="outlined"
              helperText="Minimums for approval: $1,000,000 General Liability / Commercial Auto; $2,000,000 On-Hook"
              InputProps={{
                inputProps: {
                  step: "0.01" // Allow decimals for currency
                }
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Effective Date"
              name="effectiveDate"
              type="date"
              value={formData.effectiveDate || ''}
              onChange={handleChange}
              fullWidth
              InputLabelProps={{ shrink: true }}
              variant="outlined"
              helperText="Required before the application can be approved"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Expiration Date"
              name="expirationDate"
              type="date"
              value={formData.expirationDate || ''}
              onChange={handleChange}
              fullWidth
              InputLabelProps={{ shrink: true }}
              variant="outlined"
              helperText={formData.expirationDate && new Date(formData.expirationDate) < new Date() ? 'This policy has expired' : 'Required and must be unexpired before approval'}
              error={Boolean(formData.expirationDate) && new Date(formData.expirationDate) < new Date()}
            />
          </Grid>

          {/* Contact Information */}
          <Grid item xs={12} sm={12} sx={{ mt: 2, mb: -1 }}>
             <Typography variant="subtitle2">Contact Information</Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              label="Contact Name"
              name="contactName"
              value={formData.contactName || ''}
              onChange={handleChange}
              fullWidth
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              label="Contact Email"
              name="contactEmail"
              type="email"
              value={formData.contactEmail || ''}
              onChange={handleChange}
              fullWidth
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              label="Contact Phone"
              name="contactPhone"
              value={formData.contactPhone || ''}
              onChange={handleChange}
              fullWidth
              variant="outlined"
            />
          </Grid>

          {/* Address */}
          <Grid item xs={12} sm={12} sx={{ mt: 1, mb: -1 }}>
             <Typography variant="subtitle2">Company Address</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Street 1"
              name="street1"
              value={formData.street1 || ''}
              onChange={handleChange}
              fullWidth
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Street 2"
              name="street2"
              value={formData.street2 || ''}
              onChange={handleChange}
              fullWidth
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="City"
              name="city"
              value={formData.city || ''}
              onChange={handleChange}
              fullWidth
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="State"
              name="state"
              value={formData.state || ''}
              onChange={handleChange}
              fullWidth
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Zip Code"
              name="zip"
              value={formData.zip || ''}
              onChange={handleChange}
              fullWidth
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Country"
              name="country"
              value={formData.country || ''}
              onChange={handleChange}
              fullWidth
              variant="outlined"
            />
          </Grid>

          {/* COI Upload */}
          <Grid item xs={12} sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Certificate of Insurance (COI) Document
            </Typography>
            <FormControl fullWidth>
              <Input
                id="coi-upload-input"
                type="file"
                onChange={handleFileChange}
                inputProps={{ accept: COI_ACCEPT }}
                sx={{ display: 'none' }} // Hide the default input
              />
              <label htmlFor="coi-upload-input">
                <Button variant="outlined" component="span" fullWidth>
                  Choose File
                </Button>
              </label>
              {fileName && (
                <FormHelperText sx={{ mt: 1, textAlign: 'center' }}>
                  Selected: {fileName} {coiFile ? `(${(coiFile.size / 1024).toFixed(2)} KB)` : ''}
                  {isEditMode && !coiFile && policyData?.documentPath && " (Current)"}
                </FormHelperText>
              )}
               {!fileName && isEditMode && policyData?.documentPath && (
                 <FormHelperText sx={{ mt: 1, textAlign: 'center', color: 'text.secondary' }}>
                   Current file: {policyData.documentPath.split('/').pop()} (Upload new to replace)
                 </FormHelperText>
               )}
               {!fileName && !isEditMode && (
                 <FormHelperText sx={{ mt: 1, textAlign: 'center' }}>
                   No file selected. Upload a PDF or a photo of the certificate.
                 </FormHelperText>
               )}
            </FormControl>

            {isEditMode && policyData?.documentPath && !coiFile && (
              <CoiPreview documentPath={policyData.documentPath} />
            )}
            {isEditMode && !policyData?.documentPath && !coiFile && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontStyle: 'italic' }}>
                No certificate on file for this policy.
              </Typography>
            )}

            {!isEditMode && onRequestCoi && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Save the policy first; you can then request the certificate from the applicant by editing it.
              </Typography>
            )}
            {isEditMode && onRequestCoi && (
              <Box sx={{ mt: 2, p: 1.5, border: '1px dashed', borderColor: 'divider', borderRadius: 1 }}>
                <Typography variant="subtitle2" gutterBottom>Need the certificate from the applicant?</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Emails {applicantEmail || 'the applicant'} a link (no login needed, valid 14 days) to upload the COI for this {formData.policyType || 'policy'}. It attaches here when they send it.
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={requestingCoi ? <CircularProgress size={16} /> : <EmailIcon />}
                  onClick={() => onRequestCoi({ ...policyData, policyType: formData.policyType })}
                  disabled={requestingCoi || !formData.policyType || !applicantEmail}
                >
                  {requestingCoi ? 'Sending…' : 'Request COI from Applicant'}
                </Button>
                {!applicantEmail && (
                  <FormHelperText error>This application has no email address on file.</FormHelperText>
                )}
                {coiRequest?.error && <Alert severity="error" sx={{ mt: 1 }}>{coiRequest.error}</Alert>}
                {!coiRequest?.error && (() => {
                  const forThisPolicy = (coiRequest?.requests || []).find((r) => r.policyType === formData.policyType);
                  if (!forThisPolicy) return null;
                  const justSent = coiRequest.sentFor === formData.policyType;
                  return (
                    <Alert severity={justSent ? 'success' : 'info'} sx={{ mt: 1 }}>
                      {justSent ? 'Request sent ' : 'Last requested '}
                      {new Date(forThisPolicy.requestedAt).toLocaleString()} for {formData.policyType}. The link works for 14 days.
                    </Alert>
                  );
                })()}
              </Box>
            )}
          </Grid>

        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary">
          Cancel
        </Button>
        <Button onClick={handleSave} color="primary" variant="contained">
          {isEditMode ? 'Save Changes' : 'Add Policy'}
        </Button>
      </DialogActions>
    </Dialog>

    {/* Confirmation Dialog for Adding New Policy */}
    <ConfirmationDialog
      open={addConfirmOpen}
      onClose={handleAddConfirmClose}
      onConfirm={handleAddConfirm}
      title="Confirm Add Policy"
      contentText={vendorInfo ? `Are you sure you want to add this policy for: ${vendorInfo.companyName || 'Unknown Company'} (Vendor ID: ${vendorInfo.vendorIdentifier || 'N/A'})?` : 'Are you sure you want to add this policy?'}
      confirmText="Add Policy"
      cancelText="Cancel"
    />
    </>
  );
}

export default PolicyFormDialog;